/**
 * Options for the Original Engagement picker: every Active, PendingChange or Completed engagement the
 * caller can see, loaded in full. The picker filters them as the user types, so an engagement missing
 * from this list cannot be picked at all.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useEngagementApi } from './use-engagement-api'
import type { EngagementSearchResult } from '@/types/api/engagement'
import { ENGAGEMENT_STATUS } from '@/lib/constants/engagement-codes'

interface EngagementDropdownItem {
  value: number
  label: string
  status: string
}

interface UseEngagementDropdownProps {
  selectedValue?: number
  excludeRecordId?: number
}

interface UseEngagementDropdownReturn {
  options: EngagementDropdownItem[]
  isLoading: boolean
}

// The search service returns at most 1000 rows per request.
const PAGE_SIZE = 1000

export function useEngagementDropdown({
  selectedValue,
  excludeRecordId
}: UseEngagementDropdownProps = {}): UseEngagementDropdownReturn {
  const [engagements, setEngagements] = useState<EngagementSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const engagementApi = useEngagementApi()

  // Loads can overlap (mount, then a new selection); only the latest one may write its result.
  const latestLoad = useRef(0)

  const loadEngagements = useCallback(async () => {
    const load = ++latestLoad.current
    try {
      setIsLoading(true)

      let filter = `(status eq '${ENGAGEMENT_STATUS.ACTIVE}' or status eq '${ENGAGEMENT_STATUS.PENDING_CHANGE}' or status eq '${ENGAGEMENT_STATUS.COMPLETED}')`

      // The selected engagement is included whatever its status, so it resolves to its own label.
      if (selectedValue) {
        filter = `(${filter} or recordId eq ${selectedValue})`
      }

      if (excludeRecordId) {
        filter = `(${filter}) and recordId ne ${excludeRecordId}`
      }

      // Version breaks recordId ties so that pages do not overlap or skip rows.
      const loaded: EngagementSearchResult[] = []
      let total = Infinity
      while (loaded.length < total) {
        const page = await engagementApi.searchEngagements(
          '*',
          filter,
          'recordId desc,version desc',
          PAGE_SIZE,
          loaded.length,
          'recordId,version,code,name,status'
        )
        loaded.push(...page.value)
        total = page.count
        if (page.value.length === 0) break
      }

      if (load === latestLoad.current) {
        setEngagements(loaded)
      }
    } catch (error) {
      console.error('Failed to load engagements for dropdown:', error)
      if (load === latestLoad.current) {
        setEngagements([])
      }
    } finally {
      if (load === latestLoad.current) {
        setIsLoading(false)
      }
    }
  }, [selectedValue, excludeRecordId, engagementApi])

  // Loads on mount, and again only for a selection outside the loaded list, whose status the base
  // filter excludes.
  const hasLoaded = useRef(false)
  useEffect(() => {
    if (!hasLoaded.current || (selectedValue && !engagements.some(e => e.recordId === selectedValue))) {
      hasLoaded.current = true
      loadEngagements()
    }
  }, [selectedValue])

  // Transform to dropdown format.
  // The search returns one row per engagement VERSION, so an engagement undergoing change comes back
  // as several rows that all share the same recordId. The dropdown selects by recordId (the option
  // value), so those rows collapse to duplicate values — and SearchableDropdown keys list items by
  // value, so duplicate keys make React thrash reconciliation (constant flicker). Collapse to one row
  // per recordId (preferring the live version — see below) before mapping to options.
  const options = useMemo(() => {
    // Prefer the LIVE version (Active/PendingChange, or Completed, which an engagement's live version
    // becomes in place when it completes) when collapsing, falling back to the highest version number
    // only among same-liveness rows. The base filter is status-constrained, but the
    // `or recordId eq selectedValue` clause is NOT — so for the already-selected source the search can
    // return a newer Draft/PendingReview row alongside the live one. Keeping the highest version blindly
    // would label the option e.g. 123.3 (draft) while handleCopyFromOriginal copies the live 123.2;
    // preferring live keeps the displayed version consistent with the data that actually gets copied.
    const liveStatuses = new Set<string>([
      ENGAGEMENT_STATUS.ACTIVE,
      ENGAGEMENT_STATUS.PENDING_CHANGE,
      ENGAGEMENT_STATUS.COMPLETED,
    ])
    const chosenByRecordId = new Map<number, EngagementSearchResult>()
    for (const engagement of engagements) {
      const existing = chosenByRecordId.get(engagement.recordId)
      if (!existing) {
        chosenByRecordId.set(engagement.recordId, engagement)
        continue
      }
      const candidateLive = liveStatuses.has(engagement.status)
      const existingLive = liveStatuses.has(existing.status)
      if (
        (candidateLive && !existingLive) ||
        (candidateLive === existingLive && engagement.version > existing.version)
      ) {
        chosenByRecordId.set(engagement.recordId, engagement)
      }
    }
    return Array.from(chosenByRecordId.values()).map((engagement): EngagementDropdownItem => ({
      value: engagement.recordId,
      label: `${engagement.recordId}.${engagement.version} - ${engagement.code}: ${engagement.name}`,
      status: engagement.status
    }))
  }, [engagements])

  return {
    options,
    isLoading
  }
}